import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, Info, ArrowRight, ShieldCheck, Sparkles, HelpCircle,
  FileText, Smartphone, Building2, ClipboardList, Rocket,
  MessageCircle, Calendar, Clock, CheckCircle2, MessageSquare,
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Checkbox } from "@/components/ui/checkbox";
import { BRAND } from "@/lib/brand";
import { useContent } from "@/lib/cms";
import { Logo } from "@/components/landing/Logo";

const iconMap = { FileText, Smartphone, Building2, ClipboardList, Rocket, ShieldCheck };

/* Status badge palette. */
const STATUS = {
  ready:       { tone: "text-emerald-700 bg-emerald-50 ring-emerald-200/80",  dot: "bg-emerald-500", label: "Usually ready" },
  setup:       { tone: "text-amber-700  bg-amber-50   ring-amber-200/80",     dot: "bg-amber-500",   label: "May require setup" },
  recommended: { tone: "text-blue-700   bg-blue-50    ring-blue-200/80",      dot: "bg-blue-500",    label: "Recommended" },
  optional:    { tone: "text-slate-600  bg-slate-100  ring-slate-200",        dot: "bg-slate-400",   label: "Optional" },
};

/* Server-side default. Mirrored 1:1 by backend CMS onboarding_checklist seed (v2). */
const FALLBACK = {
  title: "Before You Start Your WhatsApp Business API Onboarding",
  subtitle: "Please keep the following documents and business information ready before starting the onboarding process. This will help you complete the onboarding smoothly and avoid delays during Meta verification.",
  eyebrow: "Readiness Checklist",
  review_time_label: "Estimated review time",
  review_time_value: "2–3 minutes",
  sections: [
    { icon: "FileText", title: "Required Business Documents",
      subtitle: "Standard KYC set required by Meta for verification.",
      helper: "Keep these documents ready before starting onboarding.",
      items: [
        { label: "Company PAN",                 info: "Used to verify your legal business identity with Meta.", status: "ready" },
        { label: "GST Certificate",             info: "Provides tax registration proof for Indian businesses. Skip if not registered under GST.", status: "ready" },
        { label: "Business Registration",       info: "Certificate of Incorporation, MSME Certificate or Shop & Establishment Certificate — any one applicable document.", status: "ready" },
        { label: "Business Address Proof",      info: "Utility bill, rent agreement or bank statement — under 3 months old.", status: "ready" },
        { label: "Authorized Signatory ID",     info: "PAN + Aadhaar of the person applying on behalf of the business.", status: "ready" },
        { label: "Company Logo (SVG / PNG)",    info: "Used for your WhatsApp Business profile and onboarding records.", status: "ready" },
        { label: "Website URL",                 info: "Used to verify your business authenticity.", status: "ready" },
      ] },
    { icon: "Smartphone", title: "WhatsApp Number Requirements",
      subtitle: "The number you'll use on WhatsApp Business API.",
      helper: "Pick the phone number you plan to use before starting.",
      items: [
        { label: "Receives SMS",                                   info: "Meta sends a verification code via SMS during setup.",        status: "ready" },
        { label: "Receives Voice Calls",                           info: "Alternative verification path in case SMS fails.",             status: "ready" },
        { label: "Not linked to another WhatsApp API",             info: "If your number is already connected to another WhatsApp Business API provider, migration may be required before onboarding.", status: "setup" },
        { label: "Stays active during onboarding",                 info: "Do not port, deactivate or reuse this number while onboarding is in progress.", status: "ready" },
      ] },
    { icon: "ShieldCheck", title: "Meta Business Requirements",
      subtitle: "Meta artifacts you'll need or we'll help you create.",
      helper: "Meta Business Manager credentials speed up the flow.",
      items: [
        { label: "Meta Business Manager",              info: "Central console where your WhatsApp assets live.",     status: "setup" },
        { label: "Admin Access",                       info: "You'll need admin rights on the Business Manager.",    status: "ready" },
        { label: "Business Verification",              info: "Boosts messaging limits & unlocks the green tick.",     status: "recommended" },
        { label: "Display Name",                       info: "How your business appears inside WhatsApp chats.",      status: "ready" },
        { label: "Business Category",                  info: "Choose the industry that best matches your business.",  status: "ready" },
      ] },
    { icon: "Building2", title: "Company Information",
      subtitle: "Have these details handy — you'll enter them in the onboarding form.",
      helper: "Have this information ready to fill instantly.",
      items: [
        { label: "Business Name",              info: "Legal registered name of your business.",   status: "ready" },
        { label: "Website",                    info: "Your primary business website URL.",         status: "ready" },
        { label: "Support Email",              info: "The email displayed to your customers.",     status: "ready" },
        { label: "Support Mobile Number",      info: "The number your customers can call.",        status: "ready" },
        { label: "Business Address",           info: "Registered office address.",                 status: "ready" },
        { label: "GST Number",                 info: "If registered under GST.",                    status: "optional" },
        { label: "Working Hours",              info: "e.g. Mon–Sat, 9 AM–7 PM IST.",                status: "ready" },
      ] },
  ],
  timeline: [
    { step: "Step 1", title: "Review Requirements",       icon: "ClipboardList", current: true  },
    { step: "Step 2", title: "Complete Onboarding Form",  icon: "FileText",      current: false },
    { step: "Step 3", title: "Meta Verification",         icon: "ShieldCheck",   current: false },
    { step: "Step 4", title: "Go Live",                   icon: "Rocket",        current: false },
  ],
  help: {
    title: "Need help?",
    subtitle: "Talk to our WhatsApp Business API experts before starting your onboarding.",
    talk_label: "Talk to Expert",
    demo_label: "Book Demo",
  },
  confirm_label: "I have reviewed all the required documents and information.",
  proceed_label: "Proceed to Onboarding",
  proceed_micro: "Average onboarding time: 8–10 minutes",
  ready_flash:  "You're ready to begin onboarding.",
};

/* Checklist item row with expandable info tooltip and status pill. */
function ChecklistItem({ item, testid }) {
  const [open, setOpen] = useState(false);
  const s = STATUS[item.status] || null;
  return (
    <li data-testid={testid} className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-3 py-2.5">
        <span className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-emerald-500/20">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
        <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-2 gap-y-1">
          <span className="text-sm sm:text-[15px] font-medium text-slate-800 whitespace-nowrap">{item.label}</span>
          {item.info && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-label={`More info about ${item.label}`}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          )}
          {s && (
            <span className={`ml-auto inline-flex items-center gap-1.5 rounded-full ring-1 ${s.tone} text-[10.5px] font-semibold px-2 py-0.5 whitespace-nowrap`}>
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
              {s.label}
            </span>
          )}
        </div>
      </div>
      <AnimatePresence initial={false}>
        {open && item.info && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="pl-8 pb-3 text-xs sm:text-sm text-slate-600 leading-relaxed"
          >
            {item.info}
          </motion.p>
        )}
      </AnimatePresence>
    </li>
  );
}

function SectionCard({ section, idx }) {
  const Icon = iconMap[section.icon] || FileText;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay: idx * 0.05 }}
      data-testid={`checklist-section-${idx}`}
      className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-7 shadow-premium-sm hover:shadow-premium transition-shadow duration-[240ms]"
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 flex-shrink-0">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {section.title}
          </h3>
          {section.helper && (
            <p className="mt-0.5 text-xs sm:text-sm text-slate-500">{section.helper}</p>
          )}
        </div>
      </div>
      <ul className="mt-5">
        {(section.items || []).map((it, i) => (
          <ChecklistItem key={i} item={it} testid={`checklist-item-${idx}-${i}`} />
        ))}
      </ul>
    </motion.section>
  );
}

/* Simple inline SVG hero illustration — WhatsApp bubble + document + shield + check.
   No stock images. Lightweight (~1 KB). Hidden on mobile to preserve airy layout. */
function HeroIllustration() {
  return (
    <svg viewBox="0 0 220 200" width="220" height="200" aria-hidden="true" className="hidden md:block flex-shrink-0 text-emerald-500">
      <defs>
        <linearGradient id="hi-g1" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.15" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* backdrop */}
      <circle cx="110" cy="100" r="86" fill="url(#hi-g1)" />
      {/* Document */}
      <g transform="translate(48,42)" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="0" y="0" width="70" height="94" rx="8" fill="#ffffff" />
        <path d="M12 22 h46 M12 34 h46 M12 46 h32 M12 58 h38 M12 70 h28" strokeOpacity="0.55" />
      </g>
      {/* WhatsApp bubble */}
      <g transform="translate(122,32)">
        <circle cx="26" cy="26" r="26" fill="currentColor" />
        <path d="M15 32c0-6.6 5.4-12 12-12s12 5.4 12 12-5.4 12-12 12c-2 0-3.8-.5-5.4-1.3L15 44l2.4-6.6C15.9 35.7 15 34 15 32z" fill="#ffffff" />
      </g>
      {/* Shield */}
      <g transform="translate(30,120)" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 2 L2 10 v14 c0 12 9 20 20 22 11-2 20-10 20-22 V10 L22 2z" fill="#ffffff" />
        <path d="M12 22 l7 7 12-14" stroke="currentColor" strokeWidth="2.6" />
      </g>
      {/* Big check circle */}
      <g transform="translate(140,120)">
        <circle cx="26" cy="26" r="26" fill="#ffffff" stroke="currentColor" strokeWidth="2.2" />
        <path d="M14 26 l8 8 16-18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

/* Lightweight onboarding footer — replaces the marketing footer on THIS PAGE ONLY. */
function OnboardingLightFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white/70 backdrop-blur">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Logo />
          <span className="hidden sm:inline">·</span>
          <span>© {new Date().getFullYear()} Wappit Technologies</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy-policy" className="hover:text-slate-800" data-testid="onb-footer-privacy">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="hover:text-slate-800" data-testid="onb-footer-terms">Terms &amp; Conditions</Link>
          <a href={BRAND.whatsapp.waLink} target="_blank" rel="noreferrer" className="hover:text-slate-800" data-testid="onb-footer-support">Support</a>
        </div>
      </div>
    </footer>
  );
}

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const { data } = useContent("onboarding_checklist", FALLBACK);
  const cfg = { ...FALLBACK, ...(data || {}) };
  const [confirmed, setConfirmed] = useState(false);

  const proceed = () => { if (confirmed) navigate("/whatsapp-onboarding/apply"); };

  return (
    <div className="min-h-screen bg-slate-50/40 flex flex-col">
      <Navbar onBookDemo={() => navigate("/#lead-capture")} />

      {/* Hero */}
      <section className="pt-14 sm:pt-20 pb-8 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid md:grid-cols-[1fr_auto] items-center gap-6">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-600 inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              {cfg.eyebrow}
            </p>
            <h1
              className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="checklist-title"
            >
              {cfg.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              {cfg.subtitle}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100 px-4 py-1.5 text-sm font-semibold">
              <Clock className="h-4 w-4" />
              {cfg.review_time_label}: <span className="font-bold">{cfg.review_time_value}</span>
            </div>
          </div>
          <HeroIllustration />
        </div>
      </section>

      {/* Checklist grid — 4 sections */}
      <section className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-2">
            {(cfg.sections || []).map((s, i) => <SectionCard key={i} section={s} idx={i} />)}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-8 sm:py-12 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500 text-center">What happens next</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
            A simple 4-step journey to go live
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="checklist-timeline">
            {(cfg.timeline || []).map((t, i) => {
              const Icon = iconMap[t.icon] || ClipboardList;
              const current = !!t.current;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`relative rounded-3xl border p-5 shadow-premium-sm ${
                    current
                      ? "border-emerald-300 bg-gradient-to-br from-white to-emerald-50/60 ring-1 ring-emerald-200/60"
                      : "border-slate-200 bg-white"
                  }`}
                  data-testid={`checklist-timeline-step-${i}${current ? "-current" : ""}`}
                >
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ring-1 ring-slate-200 ${current ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white ring-emerald-400/50" : "bg-slate-100 text-slate-700"}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <p className={`mt-4 text-[11px] uppercase tracking-[0.18em] font-bold ${current ? "text-emerald-700" : "text-slate-500"}`}>{t.step}{current && " · Current"}</p>
                  <p className="mt-1 text-base font-extrabold text-slate-900 leading-tight">{t.title}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Confirmation card (moved ABOVE Need Help per new flow) */}
      <section className="pt-10 sm:pt-14 pb-8">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-premium">
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="checklist-confirm-label">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(!!v)}
                className="mt-1 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 h-5 w-5"
                data-testid="checklist-confirm-checkbox"
              />
              <span className="text-sm sm:text-[15px] text-slate-800 font-medium leading-relaxed">
                {cfg.confirm_label}
              </span>
            </label>

            {/* Success flash — appears once confirmed */}
            <AnimatePresence>
              {confirmed && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.22 }}
                  data-testid="checklist-ready-flash"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 px-3 py-1.5 text-xs sm:text-sm font-semibold"
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  {cfg.ready_flash}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-3">
              {/* Secondary ghost button — never competes with primary */}
              <Link
                to="/"
                data-testid="checklist-back-home"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 transition-colors"
              >
                Back to Home
              </Link>
              <div className="flex flex-col items-stretch sm:items-end">
                <button
                  type="button"
                  onClick={proceed}
                  disabled={!confirmed}
                  data-testid="checklist-proceed"
                  className={`inline-flex items-center justify-center gap-2 rounded-full text-sm font-bold px-6 py-3 transition-all duration-[240ms] ${
                    confirmed
                      ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-[0_16px_40px_-14px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 ring-1 ring-emerald-400/40"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  }`}
                >
                  {cfg.proceed_label} <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-2 text-[11px] text-slate-500 text-center sm:text-right">
                  {cfg.proceed_micro}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help — full-width dark navy CTA with emerald radial glow (mirrors homepage CTA style) */}
      <section className="pt-4 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl border border-slate-900/95 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-7 sm:p-9 lg:p-10 relative overflow-hidden" data-testid="checklist-help">
            <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-400 inline-flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5" /> Human support · 5-minute response
                </p>
                <h3
                  className="mt-3 text-2xl sm:text-3xl leading-tight font-extrabold text-white tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {cfg.help?.title || "Need help?"}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
                  {cfg.help?.subtitle}
                </p>
              </div>
              <div className="flex flex-wrap gap-2.5 flex-shrink-0">
                <a
                  href={BRAND.whatsapp.waLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="checklist-talk-expert"
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold px-5 py-3 shadow-[0_16px_40px_-12px_rgba(16,185,129,0.6)] transition-all hover:-translate-y-0.5 ring-1 ring-emerald-400/50"
                >
                  <MessageCircle className="h-4 w-4" /> {cfg.help?.talk_label || "Talk to Expert"}
                </a>
                <Link
                  to="/#lead-capture"
                  data-testid="checklist-book-demo"
                  className="inline-flex items-center gap-2 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 text-white text-sm font-bold px-5 py-3 backdrop-blur transition-all hover:-translate-y-0.5"
                >
                  <Calendar className="h-4 w-4" /> {cfg.help?.demo_label || "Book Demo"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-auto">
        <OnboardingLightFooter />
      </div>
    </div>
  );
}
